import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import api from '../api/api';
import '../styles/ResetPasswordPage.css';

const ResetPasswordPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [tokenValid, setTokenValid] = useState(true);

  // Verify token validity
  useEffect(() => {
    const verifyToken = async () => {
      try {
        await api.get(`/auth/reset-password/${token}/verify`);
      } catch (err) {
        console.error('Invalid or expired token:', err);
        setTokenValid(false);
        setError('This password reset link is invalid or has expired. Please request a new one.');
      }
    };

    if (token) {
      verifyToken();
    } else {
      setTokenValid(false);
      setError('Missing reset token. Please request a new password reset link.');
    }
  }, [token]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Simple validation
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const response = await api.post('/auth/reset-password', {
        token: token,
        newPassword: formData.password
      });
      
      if (response.data && response.data.success) {
        setSuccess(true);
        // Redirect to login after a short delay
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        setError('Something went wrong. Please try again.');
      }
    } catch (err) {
      console.error('Error resetting password:', err);
      setError(err.response?.data?.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  if (!tokenValid) {
    return (
      <div className="reset-password-container">
        <div className="reset-password-form-container">
          <div className="form-header">
            <h1>Invalid Reset Link</h1>
          </div>
          <div className="invalid-token-message">
            <p>{error}</p>
            <Link to="/forgot-password" className="request-new-link-btn">
              Request New Reset Link
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="reset-password-container">
      <div className="reset-password-form-container">
        <div className="form-header">
          <h1>Create New Password</h1>
          <p>Your new password must be different from previous used passwords.</p>
        </div>
        
        {success ? (
          <div className="success-message">
            <div className="success-icon">✓</div>
            <h2>Password Reset Successful</h2>
            <p>
              Your password has been reset successfully. You will be redirected to the login page.
            </p>
            <Link to="/login" className="login-now-btn">
              Login Now
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="reset-password-form">
            {error && <div className="error-message">{error}</div>}
            
            <div className="form-group">
              <label htmlFor="password">New Password</label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Enter new password"
                required
              />
              <small className="helper-text">
                Password must be at least 6 characters
              </small>
            </div>
            
            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm New Password</label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Confirm new password"
                required
              />
            </div>
            
            <button 
              type="submit" 
              className="submit-btn"
              disabled={loading}
            >
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>
            
            <Link to="/login" className="back-link">
              Back to Login
            </Link>
          </form>
        )}
      </div>
    </div>
  );
};

export default ResetPasswordPage;